const RECOVERY_STORAGE_KEY_PREFIX = "excalidraw-recovery-";
export const SCENE_WRITER_INVALIDATION_STORAGE_KEY = "excalidraw-ms:scene-writers-invalidated";
const SCENE_WRITER_INVALIDATION_GENERATION_PREFIX = `${SCENE_WRITER_INVALIDATION_STORAGE_KEY}:`;
const ACTIVE_LOGOUT_INVALIDATION = "active";
const COMPLETED_LOGOUT_INVALIDATION = "completed";
const localInvalidationGenerations = new Map<string, { published: boolean; completed: boolean }>();

export type SceneWriterInvalidationListener = (invalidated: boolean) => void;
export interface SceneWriterInvalidation {
  generation: string;
}

type SceneWriterMarkerState = "clear" | "invalidated" | "unknown";

function invalidationStorageKey(generation: string): string {
  return `${SCENE_WRITER_INVALIDATION_GENERATION_PREFIX}${generation}`;
}

function readStoredInvalidationGenerations(): Map<string, string> {
  const generations = new Map<string, string>();
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(SCENE_WRITER_INVALIDATION_GENERATION_PREFIX)) {
      generations.set(
        key.slice(SCENE_WRITER_INVALIDATION_GENERATION_PREFIX.length),
        localStorage.getItem(key) ?? "",
      );
    }
  }
  return generations;
}

function readSceneWriterMarkerState(): SceneWriterMarkerState {
  try {
    if (localStorage.getItem(SCENE_WRITER_INVALIDATION_STORAGE_KEY) !== null) {
      return "invalidated";
    }
    const storedGenerations = readStoredInvalidationGenerations();
    for (const [generation, state] of localInvalidationGenerations) {
      if (state.published && !storedGenerations.has(generation)) {
        localInvalidationGenerations.delete(generation);
      }
    }
    return storedGenerations.size > 0 || localInvalidationGenerations.size > 0
      ? "invalidated"
      : "clear";
  } catch {
    return "unknown";
  }
}

export function recoveryStorageKey(driveId: string, itemId: string): string {
  return `${RECOVERY_STORAGE_KEY_PREFIX}${driveId}-${itemId}`;
}

export function sceneOnlyStorageKey(): string {
  return "excalidraw";
}

/** Block scene writers in this tab and publish a unique invalidation generation. */
export function invalidateSceneStorageWriters(): SceneWriterInvalidation {
  const invalidation = { generation: crypto.randomUUID() };
  localInvalidationGenerations.set(invalidation.generation, {
    published: false,
    completed: false,
  });
  try {
    localStorage.setItem(
      invalidationStorageKey(invalidation.generation),
      ACTIVE_LOGOUT_INVALIDATION,
    );
    localInvalidationGenerations.set(invalidation.generation, {
      published: true,
      completed: false,
    });
  } catch {
    // Storage writes already fail when localStorage is unavailable.
  }
  return invalidation;
}

/** Durably identify a generation whose logout redirect has begun. */
export function completeSceneStorageLogoutInvalidation(
  invalidation: SceneWriterInvalidation,
): void {
  const state = localInvalidationGenerations.get(invalidation.generation);
  if (!state) return;
  localInvalidationGenerations.set(invalidation.generation, {
    ...state,
    completed: true,
  });
  try {
    localStorage.setItem(
      invalidationStorageKey(invalidation.generation),
      COMPLETED_LOGOUT_INVALIDATION,
    );
    localInvalidationGenerations.set(invalidation.generation, {
      published: true,
      completed: true,
    });
  } catch {
    // The tab-scoped handoff can still recover this generation while this page lives.
  }
}

/** Find only logout generations known to have reached redirect navigation. */
export function completedSceneStorageLogoutInvalidations(): string[] {
  try {
    const completed = new Set(
      [...readStoredInvalidationGenerations()]
        .filter(([, state]) => state === COMPLETED_LOGOUT_INVALIDATION)
        .map(([generation]) => generation),
    );
    for (const [generation, state] of localInvalidationGenerations) {
      if (state.completed) completed.add(generation);
    }
    return [...completed];
  } catch {
    return [];
  }
}

/** Withdraw only the failed logout invalidation owned by this exact attempt. */
export function restoreSceneStorageWriters(invalidation: SceneWriterInvalidation): void {
  if (!localInvalidationGenerations.has(invalidation.generation)) return;
  try {
    localStorage.removeItem(invalidationStorageKey(invalidation.generation));
  } catch {
    // Storage writes already fail when localStorage is unavailable.
  } finally {
    localInvalidationGenerations.delete(invalidation.generation);
  }
}

/** Retire a completed generation and report whether its absence was confirmed. */
export function retireSceneStorageInvalidation(generation: string): boolean {
  try {
    if (localStorage.getItem(SCENE_WRITER_INVALIDATION_STORAGE_KEY) === generation) {
      localStorage.removeItem(SCENE_WRITER_INVALIDATION_STORAGE_KEY);
    }
    localStorage.removeItem(invalidationStorageKey(generation));
    const retired =
      localStorage.getItem(SCENE_WRITER_INVALIDATION_STORAGE_KEY) !== generation &&
      localStorage.getItem(invalidationStorageKey(generation)) === null;
    if (retired) {
      localInvalidationGenerations.delete(generation);
    }
    return retired;
  } catch {
    return false;
  }
}

/** Check the current marker before restoring or writing browser-resident scene data. */
export function canWriteSceneStorage(): boolean {
  return readSceneWriterMarkerState() === "clear";
}

/**
 * Commit scene content and remove it again if invalidation was published after
 * the eligibility check but before the write completed.
 */
export function writeSceneStorage(key: string, value: string): boolean {
  if (!canWriteSceneStorage()) return false;
  try {
    localStorage.setItem(key, value);
    if (!canWriteSceneStorage()) {
      if (localStorage.getItem(key) === value) {
        localStorage.removeItem(key);
      }
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Observe logout invalidation and restoration published by another browser tab. */
export function subscribeToSceneWriterInvalidation(
  listener: SceneWriterInvalidationListener,
): () => void {
  const handleStorage = (event: StorageEvent) => {
    if (
      event.key !== SCENE_WRITER_INVALIDATION_STORAGE_KEY &&
      !event.key?.startsWith(SCENE_WRITER_INVALIDATION_GENERATION_PREFIX)
    ) {
      return;
    }
    listener(!canWriteSceneStorage());
  };

  window.addEventListener("storage", handleStorage);
  listener(!canWriteSceneStorage());
  return () => window.removeEventListener("storage", handleStorage);
}

/** Remove scene data that must not survive a user sign-out on a shared browser. */
export function clearLocalSceneStorage(): void {
  try {
    localStorage.removeItem(sceneOnlyStorageKey());
  } catch {
    // Sign-out must proceed even when browser storage is unavailable.
  }

  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(RECOVERY_STORAGE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Sign-out must proceed even when browser storage is unavailable.
  }
}

/**
 * True when the single local scene slot holds a drawing that would be replaced
 * by a copy of the session scene. Opening a cloud drawing usually leaves this
 * empty, so the stop-session confirmation only asks when there is real local
 * work at stake.
 *
 * See collaboration-protocol.md § 3.4 for how this feeds stop-session cases.
 */
export function hasLocalSceneDraft(): boolean {
  try {
    const raw = localStorage.getItem(sceneOnlyStorageKey());
    if (!raw) return false;
    const parsed: unknown = JSON.parse(raw);
    const elements = (parsed as { elements?: unknown }).elements;
    if (!Array.isArray(elements)) return false;
    return elements.some((el) => !(el as { isDeleted?: boolean })?.isDeleted);
  } catch {
    return false;
  }
}

export function shouldTreatAsOffline(err: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return true;
  }

  if (err instanceof TypeError) {
    return true;
  }

  const message =
    err instanceof Error ? err.message : typeof err === "string" ? err : String(err ?? "");
  return /offline|network|failed to fetch/i.test(message);
}
